import { z } from "zod";
import { errorCodeSchema } from "./errors";
import { idSchema } from "./ids";

/**
 * Files attached to chats, as both ends read them. The composer checks a pick against these rules
 * before it uploads, and the server checks the same rules again before it signs anything, so the
 * two can never disagree about what is acceptable.
 *
 * This module is browser-safe: it imports nothing from Node or a server.
 */

/** The most a single file may weigh, checked at start and again against the stored object. */
export const FILE_MAX_BYTES = 25 * 1024 * 1024;

/** The most files one message may carry. */
export const FILES_PER_MESSAGE_MAX = 10;

/** How long a signed upload stays valid. */
export const UPLOAD_URL_SECONDS = 900;

/** How long a signed read stays valid. A URL cannot be revoked, so this is the whole exposure. */
export const READ_URL_SECONDS = 600;

/** Uploads one person may have started and not completed within `UPLOAD_URL_SECONDS`. */
export const PENDING_UPLOADS_MAX = 20;

/** The most file IDs one `urls` call may ask about. */
export const URLS_PER_REQUEST_MAX = 50;

/**
 * The media types a chat accepts, each with the kind of thing it is: an image reaches the model as
 * a native part and text as text; a PDF and an office file (a spreadsheet, a Word or a PowerPoint
 * file, in the Office Open XML formats) are worked on with code in the sandbox, and an office file
 * has no preview and is always downloaded. SVG and HTML are absent on purpose. If either is ever
 * added it must be served `attachment` only, so user content never runs scripts from the bucket's
 * origin. The legacy binary formats (`.xls`, `.doc`, `.ppt`) and macro-enabled ones are absent too.
 */
export const FILE_TYPES = {
  "application/pdf": "document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "office",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "office",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": "office",
  "image/png": "image",
  "image/jpeg": "image",
  "image/webp": "image",
  "image/gif": "image",
  "text/plain": "text",
  "text/csv": "text",
  "text/markdown": "text",
  "application/json": "text",
} as const;

export type FileMediaType = keyof typeof FILE_TYPES;
export type FileKind = (typeof FILE_TYPES)[FileMediaType];

/**
 * The allowlisted type a declared media type names, or null for anything else. Capitals and
 * parameters (`IMAGE/PNG; charset=x`) are dropped first, so what is stored and what is signed are
 * always the bare lowercase type.
 */
export function normalizeMediaType(value: string): FileMediaType | null {
  const bare = (value.split(";")[0] ?? "").trim().toLowerCase();
  return Object.hasOwn(FILE_TYPES, bare) ? (bare as FileMediaType) : null;
}

const BY_EXTENSION: Readonly<Record<string, FileMediaType>> = {
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  webp: "image/webp",
  gif: "image/gif",
  txt: "text/plain",
  csv: "text/csv",
  md: "text/markdown",
  markdown: "text/markdown",
  json: "application/json",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
};

/**
 * The allowlisted type a file name's extension names (`bills.CSV`), or null for a name with none
 * and for any extension off the list. For when nothing better says what a file is: a browser that
 * could not tell, or a file a program made.
 */
export function mediaTypeFromName(name: string): FileMediaType | null {
  const dot = name.lastIndexOf(".");
  if (dot < 0) return null;
  const extension = name.slice(dot + 1).toLowerCase();
  return Object.hasOwn(BY_EXTENSION, extension) ? (BY_EXTENSION[extension] ?? null) : null;
}

/** Images and PDFs may open in the browser; everything else downloads. */
export function opensInline(type: FileMediaType): boolean {
  const kind = FILE_TYPES[type];
  return kind === "image" || type === "application/pdf";
}

/** The one list of states a file moves through. The database column is built from it too. */
export const FILE_STATUSES = ["pending", "ready", "failed"] as const;
export const fileStatusSchema = z.enum(FILE_STATUSES);
export type FileStatus = z.infer<typeof fileStatusSchema>;

const instantSchema = z.iso.datetime();

/** A file as a person sees it. The bucket key and the uploader never leave the server. */
export const fileViewSchema = z.strictObject({
  id: idSchema("file"),
  name: z.string(),
  mediaType: z.string(),
  size: z.int().nonnegative(),
  status: fileStatusSchema,
  /** Why a `failed` file failed: a catalog code, never exception text. */
  failure: errorCodeSchema.nullable(),
  createdAt: instantSchema,
});
export type FileView = z.infer<typeof fileViewSchema>;

/**
 * What the browser sends to begin an upload. Lenient on purpose: the service refuses a size of
 * zero, a size over the cap or a type off the list with the catalog code that names the problem,
 * which a schema failure would flatten into VALIDATION_ERROR.
 */
export const startFileInputSchema = z.strictObject({
  name: z.string(),
  mediaType: z.string(),
  size: z.number(),
});
export type StartFileInput = z.infer<typeof startFileInputSchema>;

/** The signed request the browser makes, with the headers the signature binds. */
export const uploadTargetSchema = z.strictObject({
  method: z.literal("PUT"),
  url: z.url(),
  headers: z.record(z.string(), z.string()),
  expiresAt: instantSchema,
});
export type UploadTarget = z.infer<typeof uploadTargetSchema>;

/** The answer to `start`: the pending file and where to send its bytes. */
export const startedFileSchema = z.strictObject({
  file: fileViewSchema,
  upload: uploadTargetSchema,
});
export type StartedFile = z.infer<typeof startedFileSchema>;

export const urlsInputSchema = z.strictObject({
  ids: z.array(idSchema("file")).min(1).max(URLS_PER_REQUEST_MAX),
  /**
   * Links that save the file rather than show it. Without it an image or a PDF opens in the
   * browser, and a link's `download` attribute cannot change that for another origin's file.
   */
  download: z.boolean().optional(),
});
export type UrlsInput = z.infer<typeof urlsInputSchema>;

/**
 * One answer per asked ID. A file the caller may not read looks exactly like one that does not
 * exist, so the answer never says which files are there.
 */
export const fileUrlItemSchema = z.union([
  z.strictObject({ id: idSchema("file"), url: z.url(), expiresAt: instantSchema }),
  z.strictObject({ id: idSchema("file"), refusal: z.literal("NOT_FOUND") }),
]);
export type FileUrlItem = z.infer<typeof fileUrlItemSchema>;

export const fileUrlsSchema = z.strictObject({ files: z.array(fileUrlItemSchema) });
export type FileUrls = z.infer<typeof fileUrlsSchema>;
